import type { FC } from 'react'

import { InfoBox, Input, LoaderCircle, Skeleton } from '@skytech/ui'
import { getLocaleTimezoneIsoDate } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import { useParams } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { LoadingError } from '@/components/LoadingError'
import { LocalDateTime } from '@/components/LocalDateTime'
import { useHasAssetEditingAccess } from '@/hooks/access'
import queries from '@/modules/admin/queries'
import type { AssetExtended } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import type { LeasingAssetDetailsReturn } from '@/resources/leasing'
import { leasingResources } from '@/resources/leasing'

import type { FormFields } from '../types'

export const LeasingInfo: FC<{ assetDetails: AssetExtended }> = ({ assetDetails }) => {
  const hasEditAccess = useHasAssetEditingAccess()

  const params = useParams()
  const assetId = Number(params.id)
  const translate = useTranslation()

  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))

  const { data, isLoading, isError } = useQuery(queries.adminLeasingAssetDetails(assetId), () =>
    leasingResources.getLeasingAssetDetails(assetId)
  )
  const {
    data: assetTypes,
    isLoading: isAssetTypesLoading,
    isError: isAssetTypesError,
  } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)

  if (isError || isAssetTypesError) {
    return <LoadingError />
  }
  if (isLoading || isAssetTypesLoading) {
    return <LoaderCircle />
  }

  const leasingInfo = data?.leasingInfo
  const leasingFieldLocks = data?.leasingFieldLocks
  const newLockedFieldsList = data?.lockFields

  const returnStatusOption = assetTypes?.returnStatuses.find(
    ({ id }) => id === assetDetails.returnStatusId
  )?.description

  const noLeasingInfo = Boolean(
    !leasingInfo?.endOfLeaseCurrentEndingOption &&
      !leasingInfo?.endOfLeaseAvailableEndingOptions &&
      !leasingInfo?.terminationDate &&
      !leasingInfo?.customerBuyoutPrice
  )

  const noInfoAvailable =
    Boolean(!leasingInfo?.leasePeriod) ||
    Boolean(
      !assetDetails.returnStatusId &&
        !assetDetails.result &&
        !assetDetails.resultCost &&
        !assetDetails.deleteCertificateDate &&
        !assetDetails.deleteCertificate &&
        noLeasingInfo
    )

  return (
    <div className="form-column-set">
      <div className="form-column">
        <div className="two-fields flex flex-wrap gap-1">
          <Field name="leaseStart">
            {({ field, meta }: FieldProps) => (
              <Input
                capitalizeLabel
                isFullWidth
                isDisabled={
                  leasingFieldLocks?.leaseStart ||
                  newLockedFieldsList?.includes('leaseStart') ||
                  !hasEditAccess
                }
                id="leaseStart"
                type="date"
                ariaLabel={translate('Lease start')}
                label={<Translation id="Lease start" />}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>

          <Field name="leaseEnd">
            {({ field, meta, form }: FieldProps<FormFields['leaseStart'], FormFields>) => (
              <Input
                capitalizeLabel
                isFullWidth
                isDisabled={
                  leasingFieldLocks?.leaseEnd ||
                  newLockedFieldsList?.includes('leaseEnd') ||
                  !hasEditAccess
                }
                id="leaseEnd"
                type="date"
                ariaLabel={translate('Lease end')}
                label={<Translation id="Lease end" />}
                marginBottom={16}
                min={form.values.leaseStart || ''}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>
        </div>
      </div>

      <div className={twMerge('form-column', noInfoAvailable && 'justify-center')}>
        {noInfoAvailable ? (
          <InfoBox>
            <Translation id="No further leasing information available at this time." />
          </InfoBox>
        ) : (
          <DataPoints className="m-0 p-0">
            <h3 className="!m-0 !mb-2">
              <Translation id="Leasing vendors conditions" />
            </h3>
            {Boolean(returnStatusOption) && (
              <DataPoint
                className="!m-0 !mr-4"
                headerClassName="!-top-0.5"
                header={<Translation id="Return status" />}
                value={<Translation id={returnStatusOption!} />}
              />
            )}
            {Boolean(assetDetails.result) && (
              <DataPoint
                className="!m-0 !mr-4"
                headerClassName="!-top-0.5"
                header={<Translation id="Result" />}
                value={assetDetails.result!}
              />
            )}

            {Boolean(assetDetails.resultCost) && (
              <DataPoint
                className="!m-0 !mr-4"
                headerClassName="!-top-0.5"
                header={<Translation id="Result cost" />}
                value={String(assetDetails.resultCost)}
              />
            )}

            {Boolean(leasingInfo?.leasePeriod) && (
              <DataPoint
                className="!m-0 !mr-4"
                headerClassName="!-top-0.5"
                header={<Translation id="Lease period" />}
                value={String(leasingInfo?.leasePeriod)}
              />
            )}

            {Boolean(assetDetails.deleteCertificate) && (
              <DataPoint
                className="!m-0 !mr-4"
                headerClassName="!-top-0.5"
                header={<Translation id="Delete certificate" />}
                value={assetDetails.deleteCertificate ? translate('Yes') : translate('No')}
              />
            )}

            {Boolean(assetDetails.deleteCertificateDate) && (
              <DataPoint
                className="!m-0 !mr-4"
                headerClassName="!-top-0.5"
                header={<Translation id="Delete certificate date" />}
                value={getLocaleTimezoneIsoDate(assetDetails.deleteCertificateDate!)}
              />
            )}
          </DataPoints>
        )}

        {Boolean((!isError && hasEditAccess) || leasingInfo?.leasePeriod) && !noLeasingInfo && (
          <LeasingInfoSection
            isLoading={isLoading}
            leasingInfo={leasingInfo}
            availableEndingOptions={leasingInfo?.endOfLeaseAvailableEndingOptions}
          />
        )}
      </div>
    </div>
  )
}

const LeasingInfoSection: FC<{
  isLoading: boolean
  leasingInfo: LeasingAssetDetailsReturn['leasingInfo']
  availableEndingOptions?: string
}> = ({ isLoading, leasingInfo, availableEndingOptions }) => {
  if (isLoading) {
    return <Skeleton width={200} />
  }
  if (!leasingInfo) return null

  return (
    <section>
      <DataPoints className="m-0 p-0">
        <h3 className="!m-0 !my-2">
          <Translation id="Ending options" />
        </h3>
        {Boolean(leasingInfo.endOfLeaseCurrentEndingOption || availableEndingOptions) && (
          <>
            {leasingInfo.endOfLeaseCurrentEndingOption && (
              <DataPoint
                className="!m-0 !mr-4"
                headerClassName="!-top-0.5"
                header={<Translation id="Current" />}
                value={
                  <Translation
                    id={`current_ending_option/${leasingInfo.endOfLeaseCurrentEndingOption}`}
                  />
                }
              />
            )}
            {Boolean(availableEndingOptions) && (
              <DataPoint
                className="!m-0 !mr-4"
                headerClassName="!-top-0.5"
                header={<Translation id="Available" />}
                values={availableEndingOptions
                  ?.split(',')
                  .map(option => (
                    <Translation
                      key={option}
                      id={`available_ending_option/${option}`}
                      option={option}
                    />
                  ))}
              />
            )}
          </>
        )}

        {Boolean(leasingInfo.terminationDate) && (
          <DataPoint
            className="!m-0 !mr-4"
            headerClassName="!-top-0.5"
            header={<Translation id="Termination date" />}
            value={<LocalDateTime date={leasingInfo.terminationDate} />}
          />
        )}
        {Boolean(leasingInfo.customerBuyoutPrice) && (
          <DataPoint
            className="!m-0 !mr-4"
            headerClassName="!-top-0.5"
            header={<Translation id="Customer buyout price" />}
            value={<FormattedNumberWithCurrency amount={leasingInfo.customerBuyoutPrice} />}
          />
        )}
      </DataPoints>
    </section>
  )
}
